import { useEffect, useState } from "react";
import { api, wsUrl } from "./api";

/**
 * Real-time waitlist stats hook.
 *
 * Opens a WebSocket to /api/ws/waitlist which pushes the freshest
 * {total_signups, founding_active, spots_left, ...} payload on connect and
 * again every time the counts mutate (signup, admin access flip, promotion).
 *
 * Fallbacks: does one immediate REST fetch on mount (so the number renders
 * even if the socket is slow/blocked) and reconnects on socket close with a
 * short backoff so a transient network blip doesn't freeze the ticker.
 */
export function useWaitlistStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let ws = null;
    let retryTimer = null;

    // Immediate REST hydrate so the number appears without waiting for WS
    api.get("/waitlist/stats").then((r) => { if (!cancelled) setStats(r.data); }).catch(() => {});

    const connect = () => {
      if (cancelled) return;
      try {
        ws = new WebSocket(wsUrl("/api/ws/waitlist"));
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg?.type === "waitlist_stats") {
              const { type, ...rest } = msg;
              if (!cancelled) setStats(rest);
            }
          } catch (_e) { /* ignore malformed frame */ }
        };
        ws.onclose = () => {
          if (cancelled) return;
          retryTimer = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch (_e) { /* ignore */ } };
      } catch (_e) {
        retryTimer = setTimeout(connect, 5000);
      }
    };
    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (ws) try { ws.close(); } catch (_e) { /* ignore */ }
    };
  }, []);

  return stats;
}
